import type { FirebaseApp } from 'firebase/app'
import type { Messaging } from 'firebase/messaging'

// Firebase configuration from environment variables
const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID,
	measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

// VAPID key for web push
const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY

// Cached instances to prevent re-initialization
let firebaseApp: FirebaseApp | null = null
let messaging: Messaging | null = null
let firebaseInitPromise: Promise<{
	app: FirebaseApp | null
	messaging: Messaging | null
}> | null = null

/**
 * Dynamically load and initialize Firebase
 * This reduces initial bundle size by only loading Firebase when needed
 */
const initializeFirebase = async () => {
	// Return cached promise if already initializing
	if (firebaseInitPromise) {
		return firebaseInitPromise
	}

	// Return cached instances if already initialized
	if (firebaseApp) {
		return { app: firebaseApp, messaging }
	}

	firebaseInitPromise = (async () => {
		try {
			// Dynamic import of Firebase modules
			const [{ initializeApp, getApps }, { getMessaging }] = await Promise.all([
				import('firebase/app'),
				import('firebase/messaging'),
			])

			// Check if Firebase is already initialized
			if (getApps().length === 0) {
				firebaseApp = initializeApp(firebaseConfig)
			} else {
				firebaseApp = getApps()[0]
			}

			// Only initialize messaging in browsers that support it
			if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
				messaging = getMessaging(firebaseApp)
			}

			return { app: firebaseApp, messaging }
		} catch (error) {
			console.error('Error initializing Firebase:', error)
			firebaseInitPromise = null // Reset promise on error to allow retry
			return { app: null, messaging: null }
		}
	})()

	return firebaseInitPromise
}

// Check if device is iOS mobile
const isIOSMobile = (): boolean =>
	/iPad|iPhone|iPod/.test(navigator.userAgent) &&
	!(window as unknown as { MSStream?: unknown }).MSStream

// Check if device is in standalone mode (PWA)
const isStandalone = (): boolean =>
	window.matchMedia('(display-mode: standalone)').matches ||
	(window.navigator as unknown as { standalone?: boolean }).standalone === true

/**
 * Check if FCM is supported on this platform
 * FCM is NOT supported on:
 * - iOS Safari (not in PWA mode)
 * - Browsers without service worker support
 * - Browsers without Notification API
 */
export const isFCMSupported = (): boolean => {
	// Check basic requirements
	if (typeof window === 'undefined') return false
	if (!('serviceWorker' in navigator)) return false
	if (!('Notification' in window)) return false
	if (!('PushManager' in window)) return false

	// iOS Safari doesn't support web push unless in PWA mode
	if (isIOSMobile() && !isStandalone()) {
		return false
	}

	return true
}

// Request notification permission
export const requestNotificationPermission =
	async (): Promise<NotificationPermission> => {
		try {
			if (!('Notification' in window)) {
				return 'denied'
			}
			const permission = await Notification.requestPermission()
			return permission
		} catch (error) {
			console.error('Error requesting notification permission:', error)
			return 'denied'
		}
	}

// Get FCM token
export const getFCMToken = async (): Promise<string | null> => {
	try {
		if (!isFCMSupported()) {
			console.log('FCM not supported on this platform')
			return null
		}

		// Initialize Firebase if not already done
		const { messaging: messagingInstance } = await initializeFirebase()
		if (!messagingInstance) {
			throw new Error('Failed to initialize Firebase messaging')
		}

		// Check notification permission
		const permission = Notification.permission
		if (permission !== 'granted') {
			console.log('Notification permission not granted:', permission)
			return null
		}

		// Register service worker first
		const registration = await navigator.serviceWorker.register(
			'/firebase-messaging-sw.js',
			{
				scope: '/',
			},
		)
		await navigator.serviceWorker.ready

		// Dynamic import getToken only when needed
		const { getToken } = await import('firebase/messaging')

		// Get FCM token
		const token = await getToken(messagingInstance, {
			vapidKey: vapidKey,
			serviceWorkerRegistration: registration,
		})

		return token || null
	} catch (error) {
		console.error('Error getting FCM token:', error)
		return null
	}
}

// Setup foreground message handling
export const setupForegroundMessageListener = (
	callback: (payload: unknown) => void,
): (() => void) => {
	// Return empty unsubscribe function if FCM is not supported
	if (!isFCMSupported()) {
		return () => {}
	}

	// Create an async setup function
	const setupListener = async () => {
		try {
			const { messaging: messagingInstance } = await initializeFirebase()
			if (!messagingInstance) {
				return () => {}
			}

			// Dynamic import onMessage only when needed
			const { onMessage } = await import('firebase/messaging')
			return onMessage(messagingInstance, callback)
		} catch (error) {
			console.error('Error setting up foreground message listener:', error)
			return () => {}
		}
	}

	// Store the unsubscribe function once it's ready
	let unsubscribe: (() => void) | null = null
	setupListener().then((unsub) => {
		unsubscribe = unsub
	})

	// Return a function that will unsubscribe when called
	return () => {
		if (unsubscribe) {
			unsubscribe()
		}
	}
}

/**
 * Notify service worker that an SSE event was received
 * This is used for deduplication - if SSE event is received first,
 * the push notification will be suppressed
 */
export const notifyServiceWorkerOfSseEvent = (eventId: string): void => {
	if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
		navigator.serviceWorker.controller.postMessage({
			type: 'SSE_EVENT_RECEIVED',
			eventId,
		})
	}
}

// Initialize Firebase and return instances
export const getFirebaseInstances = async () => {
	return initializeFirebase()
}
